import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../components/UploadImage";

interface MemberDetail {
  firstName: string;
  lastName: string;
  designation: string;
  image: string;
  introduction: string;
  quote: string;
  phone: string;
  email: string;
  linkedIn: string;
}

interface Props {
  form: any;
  memberData?: MemberDetail[];
  loading?: boolean;

  mode: "list" | "add" | "edit";
  setMode: (v: "list" | "add" | "edit") => void;

  editingIndex: number | null;
  setEditingIndex: (v: number | null) => void;

  handleSave: () => void;
  handleCancel: () => void;
  onDelete: (index: number) => void;
}

const emptyMember: MemberDetail = {
  firstName: "",
  lastName: "",
  designation: "",
  image: "",
  introduction: "",
  quote: "",
  phone: "",
  email: "",
  linkedIn: "",
};

const MemberTable: React.FC<Props> = ({
  form,
  memberData,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
  handleSave,
  handleCancel,
  onDelete,
}) => {
  useEffect(() => {
    if (mode === "add") {
      form.setFieldsValue({
        team: { member: [emptyMember] },
      });
    }

    if (mode === "edit" && editingIndex !== null && memberData) {
      form.setFieldsValue({
        team: { member: [memberData[editingIndex]] },
      });
    }
  }, [mode, editingIndex, memberData, form]);

  return (
    <>
      {mode === "list" && (
        <div>
          <div className="hidden sm:block rounded-[20px] overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="px-4 py-3">Image</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Introduction</th>
                  <th className="px-4 py-3">Quote</th>
                  <th className="px-4 py-3">Contact Details</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="bg-[#F9F9F9]">
                {memberData?.length ? (
                  memberData.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="px-4 py-3">
                        <Image src={item.image} width={70} />
                      </td>
                      <td className="px-4 py-3">
                        {item.firstName}{" "}{item.lastName}
                      </td>
                      <td className="px-4 py-3">{item?.designation}</td>
                      <td className="px-4 py-3">
                        <p className="line-clamp-3">{item.introduction}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="line-clamp-3">{item.quote}</p>
                      </td>
                      <td className="px-4 py-3">
                        <li className="list-disc font-medium">Phone:</li>{" "}
                        {item?.phone}
                        <li className="list-disc font-medium">Email:</li>{" "}
                        {item?.email}
                        <li className="list-disc font-medium">
                          LinkedIn URL:
                        </li>{" "}
                        {item?.linkedIn}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-3 justify-end">
                          <EditOutlined
                            className="text-primary cursor-pointer text-[20px]"
                            onClick={() => {
                              setMode("edit");
                              setEditingIndex(index);
                            }}
                          />
                          <Popconfirm
                            title="Delete this member?"
                            onConfirm={() => onDelete(index)}
                          >
                            <DeleteOutlined className="text-red-500 cursor-pointer text-[20px]" />
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-gray-500">
                      No members added
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={9} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                      className="text-primary font-semibold"
                    >
                      Add Member
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      {mode === "edit" && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["team", "member"]}>
              {(fields) =>
                fields.map((field) => (
                  <div key={field.key} className="border p-4 rounded">
                    <div className="grid grid-cols-2 gap-4">
                      <Form.Item
                        label="First Name"
                        name={[field.name, "firstName"]}
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        label="Last Name"
                        name={[field.name, "lastName"]}
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <Form.Item
                      label="Designation"
                      name={[field.name, "designation"]}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Introduction"
                      name={[field.name, "introduction"]}
                      rules={[{ required: true }]}
                    >
                      <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item label="Quote" name={[field.name, "quote"]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label="Phone" name={[field.name, "phone"]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label="Email" name={[field.name, "email"]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label="LinkedIn" name={[field.name, "linkedIn"]}>
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Image"
                      name={[field.name, "image"]}
                      rules={[{ required: true }]}
                    >
                      <UploadImage count={1} listType="picture-card" />
                    </Form.Item>
                  </div>
                ))
              }
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && (
        <div className="block sm:hidden mb-4">
          {memberData?.length ? (
            memberData.map((item, index) => (
              <div
                key={index}
                className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-3 mb-3"
              >
                <div className="flex gap-3">
                  <Image src={item.image} width={60} />
                  <div>
                    <p className="font-semibold">
                      {item.firstName} {item.lastName}
                    </p>
                    <p className="text-sm text-gray-700">{item.designation}</p>
                  </div>
                </div>

                <div>
                  <span className="font-bold text-primary">Introduction:</span>
                  <p className="text-sm">{item.introduction || "—"}</p>
                </div>

                <div>
                  <span className="font-bold text-primary">Quote:</span>
                  <p className="text-sm">{item.quote || "—"}</p>
                </div>

                <div className="text-sm">
                  <p>
                    <span className="font-bold text-primary">Phone:</span>{" "}
                    <p className="line-clamp-1">{item.phone || "—"}</p>
                  </p>
                  <p>
                    <span className="font-bold text-primary">Email:</span>{" "}
                    <p className="line-clamp-1">{item.email || "—"}</p>
                  </p>
                  <p>
                    <span className="font-bold text-primary">LinkedIn:</span>{" "}
                    <p className="line-clamp-1">
                      {item.linkedIn ? (
                        <a
                          href={item.linkedIn}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          LinkedIn Profile
                        </a>
                      ) : (
                        <span className="text-gray-400">Not available</span>
                      )}
                    </p>
                  </p>
                </div>

                <div className="flex gap-2 pt-2 pr-2">
                  <EditOutlined
                    className="text-primary text-[20px]"
                    onClick={() => {
                      setMode("edit");
                      setEditingIndex(index);
                    }}
                  />

                  <Popconfirm
                    title="Delete this member?"
                    onConfirm={() => onDelete(index)}
                  >
                    <DeleteOutlined className="text-red-500 text-lg" />
                  </Popconfirm>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">No members added</p>
          )}

          <Button
            type="dashed"
            className="text-primary font-semibold"
            icon={<PlusOutlined />}
            block
            onClick={() => {
              setMode("add");
              setEditingIndex(null);
            }}
          >
            Add Member
          </Button>
        </div>
      )}
    </>
  );
};

export default MemberTable;
